/* Asistente — la conversación sobre el grafo (pantalla 3a del rediseño).
 *
 * Lo que cambia respecto del chat anterior no es el protocolo: es QUÉ SE VE.
 *
 * · El modelo YA emitía la plantilla correcta —síntesis → cifras → secciones → cierre, con bloque
 *   ```cifras, enlaces `secop:` y sugerencias de seguimiento (ver «FORMATO DE LA RESPUESTA» en
 *   `app/llm.py`)—. El cliente lo pintaba como un muro de Markdown y esa estructura se perdía
 *   entera. Aquí la síntesis sale arriba, en serif grande y con su rótulo: es lo único que mucha
 *   gente va a leer, y es exactamente la frase que responde la pregunta.
 *
 * · El contexto de proceso deja de ser un iframe del 30 %. Se entra desde la ficha, se hereda el
 *   proceso, y se puede SOLTAR para preguntar de todo SECOP. La vuelta es a LA FICHA, no al
 *   listado: quien vino a decidir sobre un proceso vuelve a donde estaba decidiendo.
 *
 * Del protocolo no se toca NADA: el SSE, `_proceso_ctx` y la plantilla del prompt se reusan tal
 * cual. Esto es presentación.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ErrorApi, obtener } from '../api/cliente'
import { flujoEventos } from '../api/sse'
import { fecha, normalizarNombre, normalizarObjeto } from '../api/formato'
import { analizar, aTextoPlano, partirRespuesta } from '../api/markdown'
import type { Bloque, ClaseSecop, Inline } from '../api/markdown'
import type { EventoChat, Meta, RespuestaFicha } from '../api/tipos'
import { usePerfil } from '../estado/perfil'
import { Grafico } from '../ui/Grafico'
import type { MetaGrafico } from '../ui/Grafico'
import { IconoSeguir, IconoSi, IconoVolver } from '../ui/Iconos'
import './Asistente.css'

/** Una llamada a herramienta, tal como la resume el servidor para «Cómo lo averigüé». */
interface PasoTool {
  tool: string
  args?: Record<string, unknown>
  n?: number
  error?: string
}

interface Turno {
  id: number
  rol: 'yo' | 'vigia'
  texto: string
  /** Solo en los turnos del asistente, y solo cuando ya terminaron. */
  pasos?: PasoTool[]
  graficos?: { option: unknown; meta: MetaGrafico }[]
  sugerencias?: { q: string; tema: string }[]
  segundos?: number
  enCurso?: boolean
  fase?: string
}

export function Asistente() {
  const [params, setParams] = useSearchParams()
  const navegar = useNavigate()
  const { perfil, rup, marcar } = usePerfil()
  const procesoId = params.get('proceso')

  const [turnos, setTurnos] = useState<Turno[]>([])
  const [pregunta, setPregunta] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ficha, setFicha] = useState<RespuestaFicha | null>(null)
  const [meta, setMeta] = useState<Meta | null>(null)

  // Un id de sesión POR PESTAÑA, en `sessionStorage`. Con él el servidor recupera el contexto
  // rico del turno anterior (los RESULTADOS de las herramientas, no solo su texto). Sin él, cada
  // turno reconstruye la conversación desde las cadenas y el modelo pierde lo que ya consultó.
  const sesion = useRef<string>(idDeSesion())
  const corte = useRef<AbortController | null>(null)
  const fondo = useRef<HTMLDivElement>(null)

  useEffect(() => {
    obtener<Meta>('/api/oportunidades/meta').then(setMeta).catch(() => setMeta(null))
  }, [])

  useEffect(() => {
    if (!procesoId) {
      setFicha(null)
      return
    }
    const c = new AbortController()
    obtener<RespuestaFicha>(`/api/oportunidades/${procesoId}`, c.signal)
      .then(setFicha)
      .catch(() => { if (!c.signal.aborted) setFicha(null) })
    return () => c.abort()
  }, [procesoId])

  // Al cambiar de proceso se empieza otra conversación: heredar los turnos de un proceso en el
  // contexto de otro es la forma más rápida de que el modelo mezcle dos expedientes.
  useEffect(() => {
    setTurnos([])
    sesion.current = idDeSesion(true)
  }, [procesoId])

  useEffect(() => {
    fondo.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turnos])

  useEffect(() => () => corte.current?.abort(), [])

  const preguntar = useCallback(async (texto: string) => {
    const limpio = texto.trim()
    if (!limpio || ocupado) return
    setPregunta('')
    setError(null)
    setOcupado(true)

    const historia = [...turnos.map((t) => ({ role: t.rol === 'yo' ? 'user' : 'model', text: t.texto })),
                      { role: 'user', text: limpio }]
    const idYo = Date.now()
    const idVigia = idYo + 1
    setTurnos((t) => [...t,
      { id: idYo, rol: 'yo', texto: limpio },
      { id: idVigia, rol: 'vigia', texto: '', enCurso: true, fase: 'pensando' }])

    const c = new AbortController()
    corte.current = c
    const t0 = Date.now()
    const pasos: PasoTool[] = []
    const graficos: { option: unknown; meta: MetaGrafico }[] = []
    let texto_ = ''

    const parche = (cambios: Partial<Turno>) =>
      setTurnos((ts) => ts.map((t) => (t.id === idVigia ? { ...t, ...cambios } : t)))

    try {
      for await (const ev of flujoEventos<EventoChat>('/api/chat/stream', {
        messages: historia,
        session_id: sesion.current,
        // `rup` viaja en cada turno porque el servidor del chat es stateless. El PERFIL no: lo
        // lee de la cuenta.
        ...(rup ? { rup } : {}),
        ...(procesoId ? { proceso_id: procesoId } : {}),
      }, c.signal)) {
        switch (ev.type) {
          case 'status':
            parche({ fase: ev.phase })
            break
          case 'answer_delta':
            texto_ += ev.text
            parche({ texto: texto_ })
            break
          case 'answer_reset':
            // El servidor rehace la respuesta desde cero (la anti-fabricación la invalidó). Si no
            // se limpia aquí, la versión mala queda pegada delante de la buena.
            texto_ = ''
            parche({ texto: '' })
            break
          case 'answer':
            texto_ = ev.text
            parche({ texto: texto_ })
            break
          case 'tool':
            pasos.push({ tool: ev.tool, ...(ev.args ? { args: ev.args } : {}),
                         ...(ev.n !== undefined ? { n: ev.n } : {}),
                         ...(ev.error ? { error: ev.error } : {}) })
            parche({ pasos: [...pasos] })
            break
          case 'chart':
            graficos.push({ option: ev.option, meta: (ev.meta ?? {}) as MetaGrafico })
            parche({ graficos: [...graficos] })
            break
          case 'suggestions':
            parche({ sugerencias: ev.items })
            break
          default:
            break
        }
      }
      parche({ enCurso: false, segundos: (Date.now() - t0) / 1000 })
    } catch (e) {
      if (!c.signal.aborted) {
        setError(e instanceof ErrorApi ? e.message : String(e))
        parche({ enCurso: false })
      }
    } finally {
      setOcupado(false)
      corte.current = null
    }
  }, [turnos, ocupado, procesoId, rup])

  const proceso = ficha?.procesos?.[0]
  const entidad = proceso ? ficha?.entidades?.[proceso.entidad_nit] : undefined
  const corteDatos = typeof meta?.fecha_corte_grafo === 'string' ? meta.fecha_corte_grafo : null

  /** Un enlace `secop:` no navega fuera: abre ese sujeto como pregunta nueva. */
  const explorar = useCallback((clase: ClaseSecop, id: string, nombre: string) => {
    const como: Record<ClaseSecop, string> = {
      entidad: `Cuéntame sobre la entidad ${nombre} (NIT ${id}).`,
      proveedor: `Cuéntame sobre el proveedor ${nombre} (NIT ${id}).`,
      persona: `Cuéntame sobre la persona con documento ${id}.`,
      contrato: `Muéstrame la ficha del contrato ${id}.`,
    }
    void preguntar(como[clase])
  }, [preguntar])

  return (
    <div className="asistente">
      {proceso && (
        <>
          <div className="contexto">
            <button type="button" className="accion volver"
                    onClick={() => navegar(`/proceso/${proceso.id}`)}>
              <IconoVolver /> Volver a la ficha
            </button>
            <span className="contexto-pildora">
              <span className="rotulo rotulo-oro">Preguntando sobre</span>
              <b title={entidad?.nombre ?? ''}>
                {proceso.referencia || proceso.id}
                {entidad?.nombre ? ` · ${normalizarNombre(entidad.nombre)}` : ''}
              </b>
            </span>
            <span className="contexto-nota">
              Abriste el asistente desde la ficha: todo lo que preguntes se responde sobre este
              proceso hasta que sueltes el contexto.
            </span>
            <button type="button" className="soltar"
                    onClick={() => { setParams({}, { replace: true }) }}>
              Soltar el contexto y preguntar de todo SECOP <IconoSeguir />
            </button>
          </div>
          <nav className="migas" aria-label="Dónde estás">
            <Link to="/">Oportunidades</Link>
            <span aria-hidden="true">›</span>
            <Link to={`/proceso/${proceso.id}`}>
              {recortar(normalizarObjeto(proceso.descripcion || proceso.objeto || proceso.id,
                                        [entidad?.ciudad, entidad?.departamento]), 60)}
            </Link>
            <span aria-hidden="true">›</span>
            <span>Preguntas sobre este proceso</span>
          </nav>
        </>
      )}

      <div className="conversacion">
        {turnos.length === 0 && (
          <Bienvenida conProceso={Boolean(proceso)} onPreguntar={preguntar} />
        )}
        {turnos.map((t) =>
          t.rol === 'yo' ? (
            <div key={t.id} className="turno turno-yo">
              <span className="avatar avatar-yo" aria-hidden="true">Tú</span>
              <div className="burbuja">{t.texto}</div>
            </div>
          ) : (
            <div key={t.id} className="turno">
              <span className="avatar avatar-vigia" aria-hidden="true">V</span>
              <Respuesta turno={t} corteDatos={corteDatos} procesoId={procesoId}
                         onExplorar={explorar} onPreguntar={preguntar}
                         onGuardar={(nota) => procesoId && marcar(procesoId, 'sigo', undefined, nota)} />
            </div>
          ),
        )}
        {error && <p className="asistente-error">{error}</p>}
        <div ref={fondo} />
      </div>

      <div className="compositor">
        <form
          className="compositor-fila"
          onSubmit={(e) => { e.preventDefault(); void preguntar(pregunta) }}
        >
          <textarea
            value={pregunta}
            onChange={(e) => setPregunta(e.target.value)}
            onKeyDown={(e) => {
              // Enter envía, Shift+Enter salta línea. Se declara debajo, porque en un campo de
              // varias líneas nadie lo adivina.
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void preguntar(pregunta)
              }
            }}
            rows={1}
            placeholder={proceso
              ? 'Pregunta lo que necesites de este proceso o de la entidad…'
              : 'Pregunta sobre cualquier entidad, empresa, persona o contrato de SECOP…'}
            aria-label="Tu pregunta"
          />
          <button type="submit" className="boton-primario" disabled={ocupado || !pregunta.trim()}>
            {ocupado ? 'Consultando…' : 'Preguntar'}
          </button>
        </form>
        <p className="compositor-nota">
          Enter envía · Shift+Enter salta línea · las respuestas describen contratación pasada de
          datos abiertos: son antecedentes, no una acusación ni un pronóstico.
        </p>
      </div>
      {perfil.nit && !proceso && (
        <p className="asistente-pie">
          Preguntando desde tu cuenta. El asistente ve el grafo completo, no solo tus oportunidades.
        </p>
      )}
    </div>
  )
}

/* ── la respuesta ─────────────────────────────────────────────────────────── */

function Respuesta({ turno, corteDatos, procesoId, onExplorar, onPreguntar, onGuardar }: {
  turno: Turno
  corteDatos: string | null
  procesoId: string | null
  onExplorar: (clase: ClaseSecop, id: string, nombre: string) => void
  onPreguntar: (q: string) => void
  onGuardar: (nota: string) => void
}) {
  const [verPasos, setVerPasos] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [guardado, setGuardado] = useState(false)

  const { corta, resto } = useMemo(
    () => partirRespuesta(analizar(turno.texto)), [turno.texto])

  if (turno.enCurso && !turno.texto) {
    return (
      <div className="respuesta respuesta-esperando">
        <span className="rotulo">{turno.fase ?? 'pensando'}</span>
        {turno.pasos?.length ? (
          <p className="esperando-pasos">
            {turno.pasos.length === 1 ? '1 consulta al grafo' : `${turno.pasos.length} consultas al grafo`}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="respuesta">
      {corta && (
        <>
          <p className="rotulo">Respuesta corta</p>
          <p className="corta"><Trozos inline={corta} onExplorar={onExplorar} /></p>
        </>
      )}
      <Cuerpo bloques={resto} onExplorar={onExplorar} />
      {turno.graficos?.map((g, i) => <Grafico key={i} option={g.option} meta={g.meta} />)}

      {!turno.enCurso && (
        <div className="respuesta-pie">
          {turno.pasos?.length ? (
            <button type="button" className="como" aria-expanded={verPasos}
                    onClick={() => setVerPasos(!verPasos)}>
              <span aria-hidden="true">{verPasos ? '▾' : '▸'}</span> Cómo lo averigüé ·{' '}
              {turno.pasos.length === 1 ? '1 consulta al grafo' : `${turno.pasos.length} consultas al grafo`}
              {turno.segundos !== undefined && ` · ${turno.segundos.toFixed(1).replace('.', ',')} s`}
            </button>
          ) : null}
          {corteDatos && <span className="corte">Datos de SECOP II al {fecha(corteDatos)}</span>}
          <span className="respuesta-acciones">
            <button type="button" className="accion" onClick={() => {
              void navigator.clipboard?.writeText(turno.texto).then(() => {
                setCopiado(true)
                window.setTimeout(() => setCopiado(false), 1800)
              })
            }}>
              {copiado ? <><IconoSi /> Copiado</> : 'Copiar'}
            </button>
            {/* Solo con un proceso en contexto: «guardar en la ficha» necesita una ficha. */}
            {procesoId && (
              <button type="button" className="accion" disabled={guardado} onClick={() => {
                onGuardar(corta ? aTextoPlano(corta) : turno.texto.slice(0, 400))
                setGuardado(true)
              }}>
                {guardado ? <><IconoSi /> Guardado en la ficha</> : 'Guardar en la ficha'}
              </button>
            )}
          </span>
        </div>
      )}

      {verPasos && turno.pasos?.length ? (
        <ul className="pasos">
          {turno.pasos.map((p, i) => (
            <li key={i} className={p.error ? 'paso-error' : ''}>
              <b>{p.tool}</b>
              {p.n !== undefined && <span className="paso-n">{p.n} filas</span>}
              {p.error && <span className="paso-n">{p.error}</span>}
              {p.args && Object.keys(p.args).length > 0 && (
                <pre>{formatearArgs(p.args)}</pre>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {turno.sugerencias?.length ? (
        <div className="seguir">
          <span className="rotulo">Seguir preguntando</span>
          <div className="seguir-chips">
            {turno.sugerencias.map((s) => (
              <button key={s.q} type="button" className="chip-sugerencia"
                      onClick={() => onPreguntar(s.q)}>
                {s.q}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * El cuerpo de la respuesta, agrupado por sección.
 *
 * Las secciones con PROSA se numeran («1 · El precio no es la palanca»): es el desglose del
 * rediseño y se lee como una lista de razones. Las que solo traen una tabla llevan rótulo en
 * versalita, no número — numerar «A quién le ha adjudicado» junto a tres argumentos convertiría
 * un anexo de datos en una cuarta razón.
 */
function Cuerpo({ bloques, onExplorar }: {
  bloques: Bloque[]
  onExplorar: (clase: ClaseSecop, id: string, nombre: string) => void
}) {
  const secciones = agrupar(bloques)
  let n = 0
  return (
    <div className="cuerpo">
      {secciones.map((s, i) => {
        const soloTabla = s.titulo !== null && esAnexo(s.bloques)
        const numero = s.titulo !== null && !soloTabla ? ++n : null
        return (
          <section key={i} className={s.titulo === null ? 'seccion-suelta' : 'seccion'}>
            {s.titulo !== null && (
              soloTabla
                ? <span className="rotulo">{s.titulo}</span>
                : <h3>{numero !== null && <span className="seccion-n">{numero} ·</span>} {s.titulo}</h3>
            )}
            {s.bloques.map((b, j) => <Pieza key={j} bloque={b} onExplorar={onExplorar} />)}
          </section>
        )
      })}
    </div>
  )
}

/** Una sección que solo trae una tabla es un ANEXO de datos, no una razón: lleva rótulo, no
 *  número. Numerar «A quién le ha adjudicado» junto a tres argumentos la convierte en un cuarto
 *  argumento, y el desglose deja de leerse como lo que es. */
function esAnexo(bloques: Bloque[]): boolean {
  return bloques.length > 0 && bloques.every((b) => b.tipo === 'tabla')
}

function agrupar(bloques: Bloque[]): { titulo: string | null; bloques: Bloque[] }[] {
  const fuera: { titulo: string | null; bloques: Bloque[] }[] = []
  for (const b of bloques) {
    if (b.tipo === 'titulo') fuera.push({ titulo: b.texto, bloques: [] })
    else {
      if (!fuera.length) fuera.push({ titulo: null, bloques: [] })
      ;(fuera[fuera.length - 1] as { bloques: Bloque[] }).bloques.push(b)
    }
  }
  return fuera
}

function Pieza({ bloque, onExplorar }: {
  bloque: Bloque
  onExplorar: (clase: ClaseSecop, id: string, nombre: string) => void
}) {
  switch (bloque.tipo) {
    case 'parrafo':
      return <p><Trozos inline={bloque.inline} onExplorar={onExplorar} /></p>
    case 'cifras':
      return (
        <div className="tarjetas-cifra">
          {bloque.items.map((c) => (
            <div key={c.etiqueta} className="tarjeta-cifra">
              <div className="cifra">{c.valor}</div>
              <div className="rotulo">{c.etiqueta}</div>
            </div>
          ))}
        </div>
      )
    case 'lista':
      return (
        <ul className="lista">
          {bloque.items.map((it, i) => (
            <li key={i}><Trozos inline={it} onExplorar={onExplorar} /></li>
          ))}
        </ul>
      )
    case 'tabla':
      return (
        <div className="tabla-envoltura">
          <table className="tabla-respuesta">
            <thead>
              <tr>{bloque.cabeceras.map((c, i) => (
                <th key={i}><Trozos inline={c} onExplorar={onExplorar} /></th>
              ))}</tr>
            </thead>
            <tbody>
              {bloque.filas.map((f, i) => (
                <tr key={i}>{f.map((c, j) => (
                  <td key={j}><Trozos inline={c} onExplorar={onExplorar} /></td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'codigo':
      return <pre className="codigo">{bloque.texto}</pre>
    default:
      return null
  }
}

function Trozos({ inline, onExplorar }: {
  inline: Inline[]
  onExplorar: (clase: ClaseSecop, id: string, nombre: string) => void
}) {
  return (
    <>
      {inline.map((t, i) => {
        switch (t.tipo) {
          case 'fuerte':
            return <b key={i}>{t.texto}</b>
          case 'codigo':
            return <code key={i}>{t.texto}</code>
          case 'enlace':
            return (
              <a key={i} href={t.href} target="_blank" rel="noopener noreferrer">{t.texto}</a>
            )
          case 'secop':
            return (
              <button key={i} type="button" className="enlace-secop"
                      onClick={() => onExplorar(t.clase, t.id, t.texto)}>
                {t.texto}
              </button>
            )
          default:
            return <span key={i}>{t.texto}</span>
        }
      })}
    </>
  )
}

/* ── piezas menores ───────────────────────────────────────────────────────── */

const EJEMPLOS_PROCESO = [
  '¿Qué debo saber antes de presentarme en este proceso?',
  '¿A quién le ha adjudicado esta entidad en este sector?',
  '¿Con quién podría presentarme en unión temporal?',
]
const EJEMPLOS_LIBRES = [
  '¿Quiénes son los principales contratistas de la Gobernación del Magdalena?',
  '¿Qué porcentaje de la contratación va por contratación directa?',
  'Investiga a la empresa con NIT 900123456',
]

function Bienvenida({ conProceso, onPreguntar }: {
  conProceso: boolean
  onPreguntar: (q: string) => void
}) {
  return (
    <div className="bienvenida">
      <h1>{conProceso ? 'Pregunta sobre este proceso' : 'Pregunta sobre la contratación pública'}</h1>
      <p>
        {conProceso
          ? 'Consulto el grafo de SECOP II y respondo con las cifras y de dónde salen. Puedes soltar el contexto arriba si quieres preguntar de todo el país.'
          : 'Consulto el grafo de SECOP II —entidades, empresas, personas y contratos— y respondo con las cifras y de dónde salen.'}
      </p>
      <div className="seguir-chips">
        {(conProceso ? EJEMPLOS_PROCESO : EJEMPLOS_LIBRES).map((q) => (
          <button key={q} type="button" className="chip-sugerencia" onClick={() => onPreguntar(q)}>
            {q}
          </button>
        ))}
      </div>
    </div>
  )
}

/** Los argumentos de una herramienta, legibles. El Cypher exacto se muestra entero: es la
 *  transparencia que hace verificable lo que dice el asistente. */
function formatearArgs(args: Record<string, unknown>): string {
  return Object.entries(args)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join('\n')
}

function recortar(t: string, n: number): string {
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t
}

/** Un id por PESTAÑA. `sessionStorage` y no `localStorage`: dos pestañas son dos conversaciones. */
function idDeSesion(nuevo = false): string {
  const clave = 'vigia_chat_sid'
  if (!nuevo) {
    const previo = window.sessionStorage?.getItem(clave)
    if (previo) return previo
  }
  const id = `s${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
  try { window.sessionStorage?.setItem(clave, id) } catch { /* modo privado */ }
  return id
}

export const _pruebas = { agrupar, esAnexo, recortar }
